import { format, subDays } from 'date-fns';
import type { WorkItem } from '../types/models';
import { comparePriority } from './dashboard';

/**
 * Work the end-of-day close keeps carrying forward. After two weeks or more an
 * item stops counting as today's work and asks for a decision instead: still
 * doing it, delegate it or drop it. The server's rule (CarryOver.IsStale) is
 * the same; keep them in step.
 */
export const STALE_AFTER_DAYS = 14;

// Work in progress can roll too: left alone, started-then-abandoned work
// would otherwise lead every list forever.
const NOT_STALE = new Set<WorkItem['status']>(['Done', 'Cancelled', 'Deferred']);

/** The rule for one day, with its dates worked out once for a whole list. */
export function staleOn(today: Date): (item: WorkItem) => boolean {
  const todayKey = format(today, 'yyyy-MM-dd');
  const cutoff = format(subDays(today, STALE_AFTER_DAYS), 'yyyy-MM-dd');
  return item => {
    if (!item.isCarryForward || !item.originalDate || NOT_STALE.has(item.status)) return false;
    const original = item.originalDate.slice(0, 10);
    // An undated item carries the 0001-01-01 sentinel: that's no start date.
    if (original < DATED_FROM) return false;
    // Work with a slot today is today's work, however long it has rolled.
    if ((item.scheduledBlocks ?? []).some(b => b.date?.slice(0, 10) === todayKey)) return false;
    // Counted from the first date, or from when the person last said "still doing it".
    const kept = item.recommittedOn?.slice(0, 10);
    const since = kept && kept > original ? kept : original;
    return since <= cutoff;
  };
}

/** Dates before this are the "no date" sentinel (the server's CarryOver.DatedFrom). */
export const DATED_FROM = '2000-01-01';

export function isStale(item: WorkItem, today: Date): boolean {
  return staleOn(today)(item);
}

/** Most important first, then longest waiting, then oldest: the tail of the morning email's TodaysOrder. */
export function byPriorityThenAge(a: WorkItem, b: WorkItem): number {
  const rank = comparePriority(a.priority, b.priority);
  if (rank !== 0) return rank;
  const since = (w: WorkItem) => {
    const original = w.originalDate?.slice(0, 10);
    return original && original >= DATED_FROM ? original : (w.targetDate ?? '').slice(0, 10);
  };
  return since(a).localeCompare(since(b)) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
}
